import queryString from 'query-string';
import { v4 as uuidv4 } from 'uuid';
import { differenceInMilliseconds } from 'date-fns';
import { parseLinker } from 'vendor/amp-analytics-linker/linker';

interface Config {
  namespace: string;
  sequenceNumberKeyNamespace: string;
  clientGuidKey: string;
  sessionTimeout: number;
}

const SESSION_TIMEOUT = 60 * 60 * 100;
const SESSION_GUID_KEY = 'autolist-web-events-sessionGuid';
const SESSION_TOUCHED_KEY = 'autolist-web-events-sessionTouched';
const CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY = 'autolist-web-event-clientFirstTouchedTimestamp';
const SEQUENCE_NUMBER_KEY_NAMESPACE = 'autolist-web-events-sequenceNumber';
const CLIENT_GUID_KEY = 'events-clientGuid';

export function setScavengerHuntClue(): void {
  const encryptedClue =
    'Hgb2AQF4a6bs7pNFDknGQX9xG3MzDR2aO6ka+ohwDhDsVqbU+DjmkBmsPG+O1854m1FrV5QS1RkBEYrTTRlG+PZx4GHOo9eDEaTXaJxye884eEp6bhJk6dlh9MdVDZCKaxdR3pACUWIKb7CYt4rtx84mtt/JE1jhoTvR3QsMDb+9h8JK1vr8vxxC3ykZJ/GTSYHzymHI3jG5yYTOffEr2oKpIycxFjz/YJBR0H6vd5pIQwr4iBJ8aMIT/+wNlNlDq8hSuSXNIQrgbLX2rYYRc8X4rjfsp+fvBbq8yckxuomXY5AV9Vjllqexo8gC2E1OEFljHRoV6P05m9bW8E7ytrbAkoqnmB/om0nHZO8cwkVisxiArpaBM6PVhQKlgc/4SKVGJPWLq4LIibcKJFuApQCOJ152j6n69S/BJCLXeBweWqj9vTE94at7vUm0TvzcnyuaxBGbjYGCI3hgAWfZaocAxUon1YDFS9QYZuXhd8rgxsLhRYVzf+RvCK14y7PP';
  window.localStorage.setItem('scavenger-9d09', encryptedClue);
}

function clearSessionGuid(): void {
  window.localStorage.removeItem(SESSION_GUID_KEY);
}

function getAmpUserId(): string | null {
  const linkerParam = queryString.parse(window.location.search).linker;

  if (!linkerParam) {
    return null;
  }

  return parseLinker(linkerParam as string)?.userId || null;
}

export function getClientGuid(): string {
  let clientGuid = window.localStorage.getItem(CLIENT_GUID_KEY);

  if (clientGuid) {
    return clientGuid;
  }

  const ampUserId = getAmpUserId();

  if (ampUserId) {
    window.localStorage.setItem(CLIENT_GUID_KEY, ampUserId);

    return ampUserId;
  }

  clientGuid = uuidv4();
  window.localStorage.setItem(CLIENT_GUID_KEY, clientGuid);

  return clientGuid;
}

export function getSessionGuid(): string {
  let sessionGuid = localStorage.getItem(SESSION_GUID_KEY);
  if (!sessionGuid) {
    sessionGuid = uuidv4();
    localStorage.setItem(SESSION_GUID_KEY, sessionGuid);
  }
  return sessionGuid;
}

export function getClientFirstTouchedTimestamp(): number {
  const clientFirstTouchedTimestamp = window.localStorage.getItem(
    CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY
  );

  if (clientFirstTouchedTimestamp) {
    return parseInt(clientFirstTouchedTimestamp, 10);
  }

  const newClientFirstTouchedTimestamp = new Date().getTime();
  window.localStorage.setItem(
    CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY,
    String(newClientFirstTouchedTimestamp)
  );

  return newClientFirstTouchedTimestamp;
}

export function getSequenceNumber(): number {
  const sequenceNumberKey = `${SEQUENCE_NUMBER_KEY_NAMESPACE}/${getSessionGuid()}`;
  let sequenceNumber = Number(window.localStorage.getItem(sequenceNumberKey));
  if (!sequenceNumber) {
    sequenceNumber = 0;
    window.localStorage.setItem(sequenceNumberKey, String(sequenceNumber));
  }
  return sequenceNumber;
}

export function incrementSequenceNumber(): void {
  const sequenceNumberKey = `${SEQUENCE_NUMBER_KEY_NAMESPACE}/${getSessionGuid()}`;
  const sequenceNumber = getSequenceNumber() + 1;
  window.localStorage.setItem(sequenceNumberKey, String(sequenceNumber));
}

// mark the session as having started. if we have never started a session before, or we
// have reached the session timeout, then clear the session guid and return true.
// otherwise returns false
export function touchSession(): boolean {
  const currentTimeStamp = new Date().toISOString();
  const lastSessionTouchedTimeStamp = window.localStorage.getItem(SESSION_TOUCHED_KEY);
  window.localStorage.setItem(SESSION_TOUCHED_KEY, currentTimeStamp);
  if (lastSessionTouchedTimeStamp) {
    const millisElapsed = differenceInMilliseconds(
      new Date(currentTimeStamp),
      new Date(lastSessionTouchedTimeStamp)
    );
    if (millisElapsed < SESSION_TIMEOUT) {
      return false;
    }
  }
  clearSessionGuid();
  return true;
}
